import React, { useState, useEffect, useCallback } from 'react';
import { Form, Input, Button, Card, Typography, message, Spin, Select, DatePicker, InputNumber, Switch } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { apiService } from '../services/api';
import type { Rule } from 'antd/es/form';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface FieldConfig {
  type: string;
  required: boolean;
  description: string;
  label: string;
  placeholder: string;
  helpText: string;
  visible: boolean;
  order: number;
  validation: any;
  options?: Array<{ label: string; value: string }>;
}

interface SchemaResponse {
  contentType: string;
  projectType: string;
  fields: Record<string, FieldConfig>;
  metadata: {
    displayName: string;
    description: string;
  };
}

interface DynamicFormProps {
  projectId: string;
  projectType: string;
  contentType: string;
  onSubmit: (data: any) => Promise<void>;
  submitEndpoint: string;
  title: string;
  description?: string;
  initialValues?: Record<string, any>;
  extraContent?: React.ReactNode;
}

export const DynamicForm: React.FC<DynamicFormProps> = ({
  projectType,
  contentType,
  onSubmit,
  title,
  description,
  initialValues,
  extraContent,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [schema, setSchema] = useState<SchemaResponse | null>(null);
  const [schemaLoading, setSchemaLoading] = useState(true);

  const fetchSchema = useCallback(async () => {
    try {
      setSchemaLoading(true);
      const schemaData = await apiService.getContentTypeSchema(contentType, projectType);
      setSchema(schemaData);
    } catch (error) {
      message.error('Failed to load form schema');
      console.error('Schema fetch error:', error);
    } finally {
      setSchemaLoading(false);
    }
  }, [contentType, projectType]);

  useEffect(() => {
    fetchSchema();
  }, [contentType, projectType, fetchSchema]);

  useEffect(() => {
    if (initialValues && schema) {
      form.setFieldsValue(initialValues);
    }
  }, [initialValues, schema, form]);

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      await onSubmit(values);
      setSubmitted(true);
      message.success('Form submitted successfully!');
    } catch (error) {
      message.error('Failed to submit form');
      console.error('Submit error:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderField = (fieldName: string, fieldConfig: FieldConfig) => {
    const { type, required, label, placeholder, helpText, validation, options } = fieldConfig;

    const rules: Rule[] = [];
    if (required) {
      rules.push({ required: true, message: `Please enter ${label.toLowerCase()}` });
    }

    // Add type-specific validation
    if (validation.email) {
      rules.push({ type: 'email', message: 'Please enter a valid email' });
    }
    if (validation.minLength) {
      rules.push({ min: validation.minLength, message: `Minimum ${validation.minLength} characters` });
    }
    if (validation.maxLength) {
      rules.push({ max: validation.maxLength, message: `Maximum ${validation.maxLength} characters` });
    }

    const baseProps = {
      placeholder,
      help: helpText || undefined
    };

    switch (type) {
      case 'string':
        return (
          <Form.Item
            key={fieldName}
            name={fieldName}
            label={label}
            rules={rules}
          >
            <Input {...baseProps} />
          </Form.Item>
        );

      case 'text':
        return (
          <Form.Item
            key={fieldName}
            name={fieldName}
            label={label}
            rules={rules}
          >
            <TextArea rows={4} {...baseProps} />
          </Form.Item>
        );

      case 'richtext':
        return (
          <Form.Item
            key={fieldName}
            name={fieldName}
            label={label}
            rules={rules}
          >
            <TextArea rows={6} {...baseProps} />
          </Form.Item>
        );

      case 'email':
        return (
          <Form.Item
            key={fieldName}
            name={fieldName}
            label={label}
            rules={rules}
          >
            <Input type="email" {...baseProps} />
          </Form.Item>
        );

      case 'enumeration':
        return (
          <Form.Item
            key={fieldName}
            name={fieldName}
            label={label}
            rules={rules}
          >
            <Select placeholder={placeholder}>
              {options?.map(option => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        );

      case 'boolean':
        return (
          <Form.Item
            key={fieldName}
            name={fieldName}
            label={label}
            valuePropName="checked"
            rules={rules}
          >
            <Switch />
          </Form.Item>
        );

      case 'date':
        return (
          <Form.Item
            key={fieldName}
            name={fieldName}
            label={label}
            rules={rules}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        );

      case 'number':
        return (
          <Form.Item
            key={fieldName}
            name={fieldName}
            label={label}
            rules={rules}
          >
            <InputNumber style={{ width: '100%' }} {...baseProps} />
          </Form.Item>
        );

      default:
        return (
          <Form.Item
            key={fieldName}
            name={fieldName}
            label={label}
            rules={rules}
          >
            <Input {...baseProps} />
          </Form.Item>
        );
    }
  };

  if (submitted) {
    return (
      <div style={{ maxWidth: 600, margin: '0 auto', padding: '20px' }}>
        <Card>
          <div style={{ textAlign: 'center' }}>
            <CheckCircleOutlined style={{ fontSize: '48px', color: '#52c41a', marginBottom: '16px' }} />
            <Title level={3}>Form Submitted Successfully!</Title>
            <Text>
              Thank you for your submission. Our team will review it and contact you if any additional information is needed.
            </Text>
          </div>
        </Card>
      </div>
    );
  }

  if (schemaLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>
          <Text>Loading form...</Text>
        </div>
      </div>
    );
  }

  if (!schema) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Text type="danger">Failed to load form schema</Text>
      </div>
    );
  }

  // Sort fields by order
  const sortedFields = Object.entries(schema.fields)
    .filter(([_, config]) => config.visible)
    .sort(([_, a], [__, b]) => a.order - b.order);

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '20px' }}>
      <Card>
        <Title level={2}>{title}</Title>
        {description && (
          <Text type="secondary" style={{ display: 'block', marginBottom: '24px' }}>
            {description}
          </Text>
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          style={{ marginTop: '24px' }}
        >
          {sortedFields.map(([fieldName, fieldConfig]) => 
            renderField(fieldName, fieldConfig)
          )}

          {extraContent}

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} size="large">
              Submit Form
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};
